<script setup>
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import ClientBondService from '@/services/ClientBondService';
import {formatDate, formatNumber} from '@/helper';
import {useBondsPageGuard} from '@/utils/features';

/**
 * IMA-517 fáze 7: menu Dluhopisy poradce – aktivní dluhopisy, které smí prodávat jeho zprostředkovatel
 * (v prodeji, připravované i s ukončeným prodejem), a kolik jeho klientů je vlastní. Cílový trh se vyhodnocuje
 * až u konkrétního klienta. Tabulka podle vzoru (_Docs/IMA-517-dluhopisy-prototyp.html) jako seznam Dluhopisů
 * v administraci (BondsIndex.vue): stav jako štítek s filtrem ve sloupci, oko pro detail.
 *
 * Poradce vidí jen desítky dluhopisů – data se načtou jednou a řazení, filtr a stránkování běží v tabulce
 * (vue-good-table bez mode="remote").
 */
const {t} = useI18n();
// v instanci bez dluhopisů (B.I.G. Invest SK/CZ) stránka přesměruje na nástěnku
useBondsPageGuard();
const loading = ref(true);
const bonds = ref([]);

/** Barva štítku stavu prodeje – stejná jako v seznamu Dluhopisů v administraci. */
const saleStateBadge = {
  ON_SALE: 'bg-success',
  PREPARING: 'bg-primary',
  CLOSED: 'bg-secondary',
};

const columns = [
  {label: t('bonds.name'), field: 'name'},
  {label: t('bonds.isin'), field: 'isin'},
  {
    label: t('bonds.sale_state'),
    field: 'sale_state',
    sortable: false,
    filterOptions: {
      enabled: true,
      placeholder: t('bonds.all_states'),
      filterDropdownItems: ['ON_SALE', 'PREPARING', 'CLOSED'].map(state => ({value: state, text: t('bonds.sale_states.' + state)})),
    },
  },
  {label: t('bonds.coupon_rate'), field: 'coupon_rate', type: 'number'},
  {label: t('bonds.coupon_frequency'), field: 'coupon_frequency_label', sortable: false},
  {label: t('bonds.sale_to'), field: 'sale_to'},
  {label: t('bonds.maturity_date'), field: 'maturity_date'},
  {label: t('clientBonds.free_volume'), field: 'free_volume', type: 'number'},
  {label: t('clientBonds.my_clients'), field: 'clients_count', type: 'number'},
  {label: '', field: 'action', sortable: false},
];

/**
 * Řádky tabulky: volný objem = volné kusy × jmenovitá hodnota (volné kusy počítá BE z objemů ESO;
 * null = objem zatím neznáme – D2), aby šel řadit jako číslo.
 */
const rows = computed(() => bonds.value.map(bond => ({
  ...bond,
  free_volume: bond.free_pieces === null || bond.nominal_value === null ? null : bond.free_pieces * bond.nominal_value,
})));

onMounted(() => {
  ClientBondService.getAdvisorBonds().then(response => {
    bonds.value = response;
  }).finally(() => {
    loading.value = false;
  });
});

/** Volný objem v milionech měny dluhopisu, jako „mil. Kč“ ve vzoru. */
function formatFreeVolume(row) {
  return row.free_volume === null ? '---' : formatNumber(row.free_volume / 1000000, 1) + ' ' + t('bonds.millions_currency', {currency: row.currency ?? 'CZK'});
}
</script>

<template>
  <div class="page-section">
    <div class="page-section__heading">
      <div class="page-section__title">{{ t('clientBonds.menu_title') }}</div>
      <div class="page-section__subtitle">{{ t('clientBonds.advisor_subtitle') }}</div>
    </div>
  </div>
  <div class="page-section__content">
    <div class="mt-3">
      <vue-good-table
          :columns="columns"
          :rows="rows"
          :sort-options="{enabled: true, initialSortBy: {field: 'sale_to', type: 'asc'}}"
          :pagination-options="{
                  enabled: true,
                  perPageDropdown: [10, 20],
                  rowsPerPageLabel: t('strategies.entriesPerPage'),
                  nextLabel: t('strategies.next'),
                  prevLabel: t('strategies.previous'),
                  ofLabel: t('strategies.of'),
                }">
        <template #table-row="props">
          <span v-if="props.column.field === 'sale_state'" class="badge" :class="saleStateBadge[props.row.sale_state]">{{ t('bonds.sale_states.' + props.row.sale_state) }}</span>
          <span v-else-if="props.column.field === 'coupon_rate'">{{ props.row.coupon_rate === null ? '---' : formatNumber(props.row.coupon_rate) + ' %' }}</span>
          <span v-else-if="props.column.field === 'sale_to'">{{ props.row.sale_to ? formatDate(props.row.sale_to, 'd.m.Y') : '---' }}</span>
          <span v-else-if="props.column.field === 'maturity_date'">{{ props.row.maturity_date ? formatDate(props.row.maturity_date, 'd.m.Y') : '---' }}</span>
          <span v-else-if="props.column.field === 'free_volume'">{{ formatFreeVolume(props.row) }}</span>
          <span v-else-if="props.column.field === 'action'">
            <span style="font-weight: bold; float: right">
              <router-link :to="{name: 'AdvisorBondDetail', params: {id: props.row.id}}"><button type="button" class="btn btn-link"><i class="mdi mdi-eye"/></button></router-link>
            </span>
          </span>
          <span v-else>{{ props.formattedRow[props.column.field] ?? '---' }}</span>
        </template>
        <template v-slot:emptystate>
          <div class="text-center">{{ loading ? t('strategies.data_loading') : t('clientBonds.no_advisor_offer') }}</div>
        </template>
      </vue-good-table>
    </div>
  </div>
</template>
